import type AhaEditorV2Controller from '../../controllers/AhaEditorV2Controller';
import classnames from 'classnames';
import styles from './Toolbar.module.css';
import React, { useState, useCallback } from 'react';
import ToolbarMenu, { toolbarMenuKey } from './ToolbarMenu';
import { type ToolbarGroup } from './types';
import { type Editor } from 'slate';

interface ToolbarGroupMenuProps {
  editor: Editor;
  controller: AhaEditorV2Controller;
  group: ToolbarGroup;
  testId: string;
}

const ToolbarGroupMenu: React.FC<
  React.PropsWithChildren<ToolbarGroupMenuProps>
> = ({ editor, controller, group, testId }) => {
  const { menu } = group;
  const triggerStyle = menu.menuStyle ?? 'icon';
  let trigger: React.ReactNode;

  switch (triggerStyle) {
    case 'textIcon':
      trigger = (
        <>
          <i className={classnames('fa-fw', menu.icon)} />
          <span>{menu.name}</span>
        </>
      );
      break;

    case 'text':
      trigger = <span>{menu.name}</span>;
      break;

    case 'component': {
      const TriggerComponent = menu.triggerComponent;
      if (TriggerComponent) {
        trigger = <TriggerComponent editor={editor} />;
      }
      break;
    }
    case 'icon':
    default:
      trigger = <i className={classnames('fa-fw', menu.icon)} />;
      break;
  }

  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useCallback((el) => {
    if (!el) return;
    const menuOpenedHandler = (e) => {
      if (e.detail === el) {
        setIsOpen(true);
      }
    };
    const menuClosedHandler = (e) => {
      if (e.detail === el) {
        setIsOpen(false);
      }
    };

    el.addEventListener('aha-menu:open', menuOpenedHandler);
    el.addEventListener('aha-menu:close', menuClosedHandler);
  }, []);

  return (
    <aha-menu ref={menuRef}>
      <div
        slot="control"
        className={classnames(styles.toolbarButton, {})}
        data-testid={testId}
        role="button"
        aria-label={menu.name}
      >
        {trigger}
        <i className={classnames('fa-solid', 'fa-caret-down')} />
      </div>
      <aha-tooltip popover="manual" show-delay={500} click-hide>
        {menu.name}
      </aha-tooltip>
      <aha-menu-content>
        {isOpen &&
          menu.tools.map((tool) => (
            <ToolbarMenu
              key={toolbarMenuKey(tool)}
              editor={editor}
              controller={controller}
              toolOrArray={tool}
            />
          ))}
      </aha-menu-content>
    </aha-menu>
  );
};

export default ToolbarGroupMenu;
